/**
 * 左侧导航，反馈问题
 */
import { useEffect, useState } from 'react';
import BaseModal from '@/components/modal';
import { Button, Input, Upload, message } from 'antd';
import { observer, useLocalObservable } from 'mobx-react-lite';
import globalStore from '@/store/global';
import urm from '@public/urm';
import { HelpOther, HelperCore } from '@/common/helper';
import { SVGPureIcon } from '@/ui';
import userStore from '@/store/user';
import { uploadToS3 } from '@/common/upload/upload-s3';
import Conf from '@/common/conf';
import content from '@/pages/modify-image/sharpen/content';

const Component = observer(props => {
  const {
    onClose,
    onOk = () => {},
    urmTag = 'feedback',
    title = '反馈问题',
    placeholder = '请输入您要反馈的问题（200字以内）',
  } = props;

  const store = useLocalObservable(() => ({
    data: {
      imageUrls: [],
      contact: '',
      content: '',
    },

    maxImageCount: 4,

    updateData(data) {
      this.data = {
        ...this.data,
        ...data,
      };
    },
  }));

  const [submiting, setSubmiting] = useState(false);

  const handleSubmit = () => {
    const { content: comment } = store.data;
    if (!comment.trim()) {
      HelpOther.toast('请输入您要反馈的问题', 'info');
      return;
    }
    if (!store.data.contact.trim()) {
      HelpOther.toast('请输入您的联系方式', 'info');
      return;
    }
    if(submiting){
      return;
    }
    setSubmiting(true);

    urm.report({
      tag: urmTag,
      // feedback_reason: comment,
      content: comment,
      ...{
        contact: store.data.contact,
        image_url: store.data.imageUrls.map(item => item.url).join(','),
      },
    })
    .then(() => {
      onClose && onClose();
      HelpOther.toast("提交成功");
    })
    .catch(() => {})
    .finally(() => {
      setSubmiting(false);
    });
  };

  const onChange = evt => {
    store.updateData({
      content: evt.target.value.slice(0, 200),
    });
  };

  const handleUpload = ({ file }) => {
    const { type } = file;
    if(!/^image/.test(type)){
      message.error("请上传图片文件");
      return;
    }

    uploadToS3(file)
    .then(res => {
      const image = res.data;
      if(!image){
        HelpOther.toast(`${file.name}上传失败，请重试`);
        let reportMessage = `问题反馈-上传图片失败\n${file.name}`;
        userStore.reportToNRobot(reportMessage);
        return;
      }
      const result = [
        ...store.data.imageUrls,
        ...[HelperCore.copyObj(image, ['url'])],
      ];
      result.length = Math.min(result.length, store.maxImageCount);
      store.updateData({
        imageUrls: result,
      });
    })
  };
  const handleDeleteImage = index => {
    store.updateData({
      imageUrls: store.data.imageUrls.filter((_, _index) => _index != index),
    });
  };

  return (
    <div className="w-480px max-w-full select-none px-24px pt-32px pb-24px">
      <div className="text-primary text-24px font-bold leading-30px mb-24px">
        {title}
      </div>
      <div className="text-16px leading-24px mb-16px tracking-normal">反馈产品问题或提出对{Conf.appName}的建议:</div>
      <div className={`relative h-228px flex flex-col border-1px border-button-bg-active rounded-16px focus-within:border-border-normal overflow-hidden ${globalStore.isDark ? 'bg-[#45454e]' : 'bg-secondary'}`}>
        <Input.TextArea
          className={`!p-16px !border-none !h-0 flex-grow !max-h-228px !text-16px !leading-24px text-primary-opacity text-opacity-80 !outline-none ${globalStore.isDark ? '!bg-[#45454e]' : '!bg-secondary'}`}
          maxLength={200}
          style={{ 
            resize: 'none',
            boxShadow: "none",
          }}
          onInput={onChange}
          placeholder={placeholder}
        />
        <div className="flex justify-end p-16px">
          <span className='text-primary-opacity text-opacity-60'>{store.data.content.length}</span><span className='text-primary-opacity text-opacity-30'>/200</span>
        </div>
      </div>
      <div className="mt-16px rounded-12px overflow-hidden bg-secondary border-1px border-button-bg-active focus-within:border-border-normal">
        <Input
          style={{ 
            '--font-size': '16px',
            resize: 'none',
            boxShadow: "none",
          }}
          className={`!h-48px !rounded-12px !border-none placeholder:text-[#000] ${globalStore.isDark ? '!bg-[#45454e]' : '!bg-secondary'}`}
          placeholder="QQ/邮箱(任选其一)"
          maxLength={50}
          value={store.data.contact}
          onInput={event => {
            store.updateData({ contact: event.target.value });
          }}
        />
      </div>
      <div className="flex flex-wrap content-start mt-16px">
        {store.data.imageUrls.map(({ url: image }, index) => {
          return (
            <div
              className={["size-98px rounded-12px vh-center relative", { "mr-12px": index < 3 }]}
              style={{
                background: `url(${image}) center center / cover no-repeat`,
              }}
              key={image + index}
            >
              <div
                onClick={() => {
                  handleDeleteImage(index);
                }}
                className="absolute -top-5px -right-5px w-20px h-20px rounded-full bg-black bg-opacity-50 vh-center cursor-pointer"
              >
                <SVGPureIcon name="x" size={10} iconClass="text-white" />
              </div>
            </div>
          );
        })}
        {store.data.imageUrls.length < store.maxImageCount && (
          <Upload
            multiple
            accept="image/*"
            showUploadList={false}
            className="uploader-without-filelist"
            customRequest={handleUpload}
          >
            <div className={["size-98px rounded-12px vh-center !flex-col", { "mr-12px": store.data.imageUrls.length < 3 }, globalStore.isDark ? 'bg-[#45454e]' : 'bg-card-body-3']}>
              <SVGPureIcon
                name="add_file"
                size={32}
                iconClass="text-[#9a9a9a]"
              />
              <div className='mt-4px text-primary-opacity text-opacity-30'>{store.data.imageUrls.length}/{store.maxImageCount}</div>
            </div>
          </Upload>
        )}
      </div>
      <div className="mt-48px">
        <div
          className={`h-48px flex items-center justify-center text-16px font-bold rounded-16px text-white ${globalStore.isDark ? 'bg-[#45454e]' : 'bg-black'}`}
          onClick={evt => {
            handleSubmit();
          }}
        >
          提交
        </div>
      </div>
    </div>
  );
});

const ReportModal = {
  self: null,
  id: null,
  show(data) {
    return new Promise(resolve => {
      const handleOk = ok => {
        resolve(ok);
        this.close();
      };

      this.self = BaseModal.show({
        borderRadius: 20,
        content: (
          <Component
            {...data}
            onClose={() => handleOk(false)}
            onOk={() => handleOk(true)}
          />
        ),
        wrapClassName: 'nw-base-modal nw-base-modal-p-0',
        afterClose: () => {
          globalStore.delModal(this.id);
          this.id = null;
          data?.afterClose?.();
        },
      });
      this.id = globalStore.regModal(this);
    });
  },
  close() {
    if (this.self) {
      globalStore.delModal(this.id);
      this.id = null;
      this.self.destroy();
      this.self = null;
    }
  },
};

export default ReportModal;
